import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Address } from 'src/app/models';
import { LocationService } from 'src/app/services/location.service';
import { MessageUtil } from 'src/app/utils/message.util';
import { InputMaskModule } from 'primeng/inputmask';
import { DropdownModule } from 'primeng/dropdown';
import { TranslateModule } from '@ngx-translate/core';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  standalone: true,
  selector: 'app-address-temp',
  templateUrl: './address-temp.component.html',
  styleUrls: ['./address-temp.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputMaskModule,
    DropdownModule,
    TranslateModule,
    AutoCompleteModule,
    InputTextModule,
    TooltipModule,
  ],
})
export class AddressTempComponent implements OnInit, OnChanges {
  @Input() address: Address = new Address();
  @Input() required: boolean = true;
  @Input() addressType: string = 'default';

  @Output() finishEvent: EventEmitter<any> = new EventEmitter<any>();

  addressForm = new UntypedFormGroup({});
  errorMessage: string;
  countries: any[] = [];
  states: any[] = [];
  cities: any[] = [];
  zipMask: string = '99999-999';

  constructor(
    public dialogRef: DynamicDialogRef,
    private locationService: LocationService,
    private messageUtil: MessageUtil,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.loadCountries();
    this.startForm(this.address);
    
    this.updateZipMask(this.address.country || 'BR');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['address'] && !changes['address'].firstChange) {
      this.startForm(this.address);
    }
  }

  startForm(address: Address): void {
    if (!address) {
      address = new Address();
    }

    if (address.state && address.state.length <= 3 && !address._stateCode) {
      address._stateCode = address.state;
    }

    const validators = this.required ? [Validators.required] : [];
    
    this.addressForm = new UntypedFormGroup({
      id: new UntypedFormControl(address.id),
      zip: new UntypedFormControl(address.zip, validators),
      country: new UntypedFormControl(address.country || 'BR', validators),
      state: new UntypedFormControl(address.state, validators),
      city: new UntypedFormControl(address.city, validators),
      district: new UntypedFormControl(address.district, validators),
      street: new UntypedFormControl(address.street, validators),
      number: new UntypedFormControl(address.number, validators),
      complement: new UntypedFormControl(address.complement),
    });

    this.addressForm.valueChanges.subscribe((values) => {
      Object.assign(this.address, values);
      this.finishEvent.emit({ addressForm: this.addressForm });
    });

    // Emite uma vez na inicialização para que o componente pai já tenha a
    // referência do form (necessário para marcar os campos como touched e
    // destacar inválidos em vermelho ao salvar, mesmo sem o usuário digitar).
    this.finishEvent.emit({ addressForm: this.addressForm });

    if (address.country) {
      this.loadStates(address.country);
      this.updateZipMask(address.country);
      if (address.state) {
        this.loadCities(address.country, address.state, address.city);
      }
    } else {
      this.updateZipMask('BR');
    }
  }

  loadCountries(): void {
    this.countries = [{ label: 'Selecione...', value: null }].concat(
      this.locationService.getCountries().map((c) => ({ label: c.name, value: c.code }))
    );
  }

  getCountryNameByCountryIso2(iso2: string): string {
    const country = this.countries?.find(c => c.value === iso2);
    if (!country) return '';

    return country.label;
  }

  getStateNameByCode(stateCode: string): string {
    const state = this.states?.find(s => s.value === stateCode || s.code === stateCode);
    return state ? state.label : '';
  }

  loadStates(_countryId: string): void {
    this.states = [{ label: 'Selecione...', value: null }].concat(
      this.locationService
        .getStates()
        .map((s) => ({ label: s.name, value: s.id, code: s.id }))
    );
  }

  loadCities(countryId: string, stateId: string, selectedCity: string = null): void {
    if (!countryId || !stateId) return;
    
    this.locationService.getCities(countryId, stateId).subscribe({
      next: (data) => {
        this.cities = data.map((c) => ({ label: c.name, value: c.name }));

        if (selectedCity) {
          const foundCity = this.cities.find(c => c.value.toLowerCase() === selectedCity.toLowerCase());
          
          if (foundCity) {
            this.addressForm.get('city').setValue(foundCity.value);
          } else {
            this.cities.push({ label: selectedCity, value: selectedCity });
            this.addressForm.get('city').setValue(selectedCity);
          }
        }
      },
      error: (error) => {
        this.cities = [];
        console.error('Error loading cities:', error);
        
        if (selectedCity) {
           this.cities.push({ label: selectedCity, value: selectedCity });
           this.addressForm.get('city').setValue(selectedCity);
        }
      }
    });
  }

  updateZipMask(_countryCode: string): void {
    this.zipMask = '99999-999'; // Default to Brazil format
  }

  getCep(): void {
    this.spinner.show();
    const zipCode = this.addressForm.get('zip').value;
    
    if (!zipCode || zipCode.trim() === '') {
      this.spinner.hide();
      return;
    }

    const currentCountry = this.addressForm.get('country').value || 'BR';
    const cleanZipCode = zipCode.trim();

    if (currentCountry === 'BR') {
      const onlyDigits = cleanZipCode.replace(/\D/g, '');
      this.locationService.getCep(onlyDigits).subscribe({
        next: (data) => {
          if (data !== null) {
            const address = new Address();
            address.zip = cleanZipCode;
            address.country = currentCountry;

            const ufCode = data.uf;
            address._stateCode = ufCode;
            address.state = ufCode;
            address.city = this.firstLetterToUpperCase(data.cidade);
            address.district = this.firstLetterToUpperCase(data.bairro);
            address.street = this.firstLetterToUpperCase(data.logradouro);

            this.loadAddressDataForCEP(this.getCountryNameByCountryIso2(currentCountry), address);
          } else {
            this.messageUtil.generateMessage(
              'warning',
              'SUMMARY.WARNING',
              'MSG.CEP-INVALID'
            );
            this.spinner.hide();
          }
        },
        error: (error) => {
          this.spinner.hide();
          this.errorMessage = error;
          this.messageUtil.generateMessage(
            'error',
            'SUMMARY.ERROR',
            'Erro ao buscar CEP'
          );
        }
      });
    } else {
      this.spinner.hide();
    }
  }

  private loadAddressDataForCEP(_countryName: string, address: Address): void {
    this.addressForm.patchValue({
      state: address.state,
      district: address.district,
      street: address.street
    });

    this.loadStates(address.country);
    this.loadCities(address.country, address.state, address.city);
    this.spinner.hide();
  }


  onCountryChange(countryCode: string): void {
    this.addressForm.patchValue({
      zip: '',
      state: null,
      city: null,
      street: '',
      district: '',
      number: '',
      complement: ''
    });
    
    this.cities = [];
    this.states = [];
    
    this.loadStates(countryCode);
    
    this.updateZipMask(countryCode);
    
    this.address.country = countryCode;
    this.finishEvent.emit({ addressForm: this.addressForm });
  }

  private firstLetterToUpperCase(text: string): string {
    if (!text) return '';
    const words = text.toLowerCase().split(' ');
    for (let a = 0; a < words.length; a++) {
      const w = words[a];
      if (w[0] !== undefined) {
        words[a] = w[0].toUpperCase() + w.slice(1);
      }
    }
    return words.join(' ');
  }

  getErrorTooltip(control: any): string {
    if (!control || !control.errors) return '';

    return Object.keys(control.errors).map(error => {
      switch (error) {
        case 'required': return 'Campo obrigatório';
        default: return error;
      }
    }).join('\n');
  }
}
